import { useRef, useState } from "react"
import { motion, useReducedMotion } from "motion/react"
import { CheckCircle2, UploadCloud } from "lucide-react"

// Resume upload in beUI's dropzone pattern (blocks/file-upload): the whole
// target is one button around the same hidden file input the flows have always
// used, so form wiring and what the server receives are unchanged. Controlled
// on purpose — beUI's queue block simulates multi-file uploads with its own
// progress lifecycle, which is not what either host flow does (they upload
// immediately and own the busy/stored display).

interface ResumeDropzoneProps {
  title: string
  description: string
  hint?: string
  accept?: string
  /** Upload ceiling in MB — files past it are rejected before any request. */
  maxSizeMb?: number
  /** A resume already lives server-side (the profile keeps one on file). */
  stored?: boolean
  /** Chosen-file name (interview flow keeps the File in its own state). */
  fileName?: string | null
  busy?: boolean
  busyLabel?: string
  disabled?: boolean
  browseLabel?: string
  /** Called with a readable message when a chosen file fails validation. */
  onReject?: (message: string) => void
  onFile: (file: File) => void
}

/** accept is an extension list ('.pdf' or '.pdf,.doc'). The input's accept
    attribute is a picker hint, not a gate (and the drop path never consults
    it), so both paths check the extension here. */
function fileMatchesAccept(file: File, accept: string): boolean {
  const extensions = accept
    .split(',')
    .map(ext => ext.trim().replace(/^\./, '').toLowerCase())
    .filter(Boolean)
  if (extensions.length === 0) return true
  const name = file.name.toLowerCase()
  return extensions.some(ext => name.endsWith(`.${ext}`))
}

export function ResumeDropzone({
  title,
  description,
  hint,
  accept = '.pdf',
  maxSizeMb = 10,
  stored = false,
  fileName,
  busy = false,
  busyLabel = 'Uploading…',
  disabled = false,
  browseLabel = 'Choose file',
  onReject,
  onFile,
}: ResumeDropzoneProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  // dragenter fires again for every child the pointer crosses, so a depth
  // counter is the only reliable leave signal.
  const depthRef = useRef(0)
  const [over, setOver] = useState(false)
  const reduce = useReducedMotion() ?? false

  const done = Boolean(fileName) || stored

  const kinds = accept
    .split(',')
    .map(ext => ext.trim().replace(/^\./, '').toUpperCase())
    .filter(Boolean)
    .join(' / ')

  // Shared by drop and browse so both paths enforce the limits the hint
  // promises, rejecting before anything reaches the server.
  const pick = (file: File | undefined) => {
    if (!file) return
    if (!fileMatchesAccept(file, accept)) {
      onReject?.(`“${file.name}” isn't a ${kinds} file.`)
      return
    }
    if (file.size > maxSizeMb * 1024 * 1024) {
      onReject?.(`“${file.name}” is over the ${maxSizeMb} MB limit.`)
      return
    }
    onFile(file)
  }

  const onDragEnter = (event: React.DragEvent) => {
    event.preventDefault()
    if (disabled) return
    depthRef.current += 1
    setOver(true)
  }
  const onDragLeave = (event: React.DragEvent) => {
    event.preventDefault()
    depthRef.current = Math.max(0, depthRef.current - 1)
    if (depthRef.current === 0) setOver(false)
  }
  const onDrop = (event: React.DragEvent) => {
    event.preventDefault()
    depthRef.current = 0
    setOver(false)
    if (disabled) return
    pick(event.dataTransfer.files?.[0])
  }

  const heading = busy
    ? busyLabel
    : over
      ? 'Drop to upload'
      : fileName
        ? fileName
        : stored
          ? 'Resume on file'
          : title

  return (
    <div>
      <motion.button
        type="button"
        disabled={disabled || busy}
        data-dragging={over}
        onClick={() => inputRef.current?.click()}
        onDragEnter={onDragEnter}
        onDragOver={(event) => event.preventDefault()}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        animate={reduce ? undefined : { scale: over ? 1.006 : 1 }}
        whileTap={reduce ? undefined : { scale: 0.995 }}
        transition={{ type: "spring", stiffness: 500, damping: 30, mass: 0.6 }}
        className="wresume-drop group relative flex w-full items-center gap-4 overflow-hidden rounded-3xl border border-dashed border-border bg-background p-5 text-left outline-none transition-[border-color] duration-200 hover:border-foreground/40 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background data-[dragging=true]:border-foreground disabled:pointer-events-none disabled:opacity-55"
      >
        <motion.span
          aria-hidden="true"
          className="grid h-14 w-14 shrink-0 place-items-center rounded-[1.25rem] bg-muted"
          style={{ color: done ? "var(--success)" : "var(--fg)" }}
          animate={reduce ? undefined : { y: over ? -2 : 0 }}
          transition={{ duration: 0.16 }}
        >
          {done ? <CheckCircle2 className="h-6 w-6" /> : <UploadCloud className="h-6 w-6" />}
        </motion.span>

        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold text-foreground">{heading}</span>
          <span className="mt-0.5 block text-xs text-muted-foreground">
            {description}
            {hint ? <span className="mt-0.5 block">{hint}</span> : null}
          </span>
        </span>

        <span className="shrink-0 rounded-full border border-border px-3.5 py-2 text-xs font-medium text-foreground transition-colors duration-150 group-hover:bg-muted">
          {browseLabel}
        </span>
      </motion.button>

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="sr-only"
        disabled={disabled}
        onChange={(event) => {
          pick(event.target.files?.[0])
          event.target.value = ''
        }}
      />
    </div>
  )
}
